'use client';

/**
 * useChargeProcessing - Charge payment state management hook
 *
 * Manages processing state that can be safely passed to UI components.
 * The UI can remount without losing state since state lives in this hook.
 *
 * Used by: PurchaseFlow (to manage state for ChargePaymentComponent)
 */

import { useState, useCallback } from 'react';
import type {
  PaymentProcessingResult,
  ChargeProcessingStatus,
  ChargeProcessingState,
  UseChargeProcessingReturn
} from '../types';

const initialState: ChargeProcessingState = {
  status: 'idle',
  message: '',
  result: null,
  error: null
};

export const useChargeProcessing = (): UseChargeProcessingReturn => {
  const [state, setState] = useState<ChargeProcessingState>(initialState);

  const updateStatus = useCallback((status: ChargeProcessingStatus, message = '') => {
    setState(prev => ({
      ...prev,
      status,
      message,
      ...(status === 'processing' ? { result: null, error: null } : {})
    }));
  }, []);

  const setSuccess = useCallback((result: PaymentProcessingResult) => {
    setState({
      status: 'success',
      message: result.okMessage || 'Payment successful!',
      result,
      error: null
    });
  }, []);

  const setError = useCallback((error: PaymentProcessingResult) => {
    setState({
      status: 'error',
      message: error.errorMessage || 'Payment failed',
      result: null,
      error
    });
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const isProcessing = state.status !== 'idle'
    && state.status !== 'success'
    && state.status !== 'error';

  return {
    state, updateStatus, setSuccess, setError, reset, isProcessing
  };
};
